import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import { AlertTriangle, CheckCircle, Loader2, MessageSquareWarning, Upload, Users, X } from 'lucide-react';
import { nomineeCorrectionService } from '../../../services/api';
import { uploadToFirebase } from '../Users/paymentFormUtils';
import { storageEnv } from '../../../config/firebase';
import { valueTypeLabel } from './investorProfile/shared';
import { DatePicker } from '../../common/DatePicker';

// Customer side of a nominee correction request: opened from the link an employee
// shares (/nominee-correction?token=…). Only the fields the employee flagged are
// editable; everything else is shown read-only for context. Rules are enforced
// again on the server (routers/nominee_corrections.py) — this page only guides.

// Must match utils/nominee_corrections.RELATIONS / GENDERS on the backend.
const RELATIONS = ['Father', 'Mother', 'Son', 'Daughter', 'Spouse', 'Brother', 'Sister', 'Guardian'];
const GENDERS = ['Male', 'Female', 'Other'];

type FieldKind = 'text' | 'mobile' | 'aadhar' | 'pan' | 'date' | 'textarea' | 'pct' | 'relation' | 'gender' | 'doc';

const FIELDS: Record<string, { label: string; prop: string; kind: FieldKind }> = {
    name: { label: 'Nominee Full Name', prop: 'nominee_full_name', kind: 'text' },
    mobile: { label: 'Mobile Number', prop: 'mobile_no', kind: 'mobile' },
    relation: { label: 'Relationship', prop: 'relation', kind: 'relation' },
    aadhar: { label: 'Aadhaar Number', prop: 'aadhar_number', kind: 'aadhar' },
    gender: { label: 'Gender', prop: 'gender', kind: 'gender' },
    pan: { label: 'PAN Number', prop: 'pancard_number', kind: 'pan' },
    dob: { label: 'Date of Birth', prop: 'dob', kind: 'date' },
    address: { label: 'Address', prop: 'address', kind: 'textarea' },
    allocation_pct: { label: 'Allocation %', prop: 'allocation_percentage', kind: 'pct' },
    doc_aadhar: { label: 'Aadhaar Card Photo', prop: 'aadhar_photo_url', kind: 'doc' },
    doc_pan: { label: 'PAN Card Photo', prop: 'pancard_photo_url', kind: 'doc' },
    doc_guardian: { label: 'Guardian Certificate', prop: 'guardian_certificate_link', kind: 'doc' },
};

const inr = (v: any) => {
    const n = Number(v);
    return v == null || isNaN(n) ? '—' : n.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
};

const sanitize = (kind: FieldKind, v: string) => {
    switch (kind) {
        case 'text': return v.replace(/[^A-Za-z .]/g, '');
        case 'mobile': return v.replace(/\D/g, '').slice(0, 10);
        case 'aadhar': return v.replace(/\D/g, '').slice(0, 12);
        case 'pan': return v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
        case 'pct': return v.replace(/[^0-9.]/g, '').slice(0, 6);
        default: return v;
    }
};

const inputCls = (err?: string) =>
    `w-full px-3 py-2.5 rounded-xl border text-sm outline-none transition-colors ${err ? 'border-red-300 bg-red-50/50' : 'border-slate-200 focus:border-[#1B4332] focus:ring-1 focus:ring-[#1B4332]/10 bg-white'}`;

const DocField: React.FC<{
    label: string; current?: string; value: string; error?: string;
    uploadPath: (file: File) => string; onChange: (url: string) => void;
}> = ({ label, current, value, error, uploadPath, onChange }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [uploadError, setUploadError] = useState('');
    const replaced = value && value !== current;

    const onFile = async (file: File) => {
        setUploading(true);
        setUploadError('');
        try {
            let finalFile = file;
            if (file.type.startsWith('image/')) {
                try { finalFile = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true }); } catch { /* use original */ }
            }
            const url = await uploadToFirebase(finalFile, uploadPath(file), setProgress);
            onChange(url);
        } catch {
            setUploadError('Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">On file</p>
                    {current
                        ? <img src={current} alt={`Current ${label}`} className="w-full h-28 object-contain rounded-lg border border-slate-200 bg-slate-50" />
                        : <div className="h-28 rounded-lg border border-dashed border-slate-200 flex items-center justify-center text-xs text-slate-400">Not uploaded</div>}
                </div>
                <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Replacement</p>
                    {replaced ? (
                        <div className="relative">
                            <img src={value} alt={`New ${label}`} className="w-full h-28 object-contain rounded-lg border border-emerald-300 bg-emerald-50/50" />
                            <button type="button" onClick={() => onChange(current || '')}
                                className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-white/90 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-full shadow">
                                <X size={13} />
                            </button>
                        </div>
                    ) : (
                        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
                            className={`w-full h-28 flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed transition-all ${error ? 'border-red-300 bg-red-50/50' : 'border-slate-200 bg-slate-50 hover:border-blue-300 hover:bg-blue-50/50'}`}>
                            {uploading
                                ? <><Loader2 size={18} className="animate-spin text-blue-500" /><span className="text-xs text-blue-500 font-medium">{progress}%</span></>
                                : <><Upload size={18} className="text-slate-400" /><span className="text-xs text-slate-500">Upload new</span></>}
                        </button>
                    )}
                </div>
            </div>
            <input ref={inputRef} type="file" accept="image/*,application/pdf" hidden
                onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
            {(error || uploadError) && <p className="text-[11px] text-red-500 font-medium">{error || uploadError}</p>}
        </div>
    );
};

const NomineeCorrectionPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [correction, setCorrection] = useState<any | null>(null);
    const [values, setValues] = useState<Record<string, Record<string, string>>>({});
    const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
    const [formError, setFormError] = useState<string | null>(null);
    const [sharedUnits, setSharedUnits] = useState<Record<string, any[]> | null>(null);
    const [confirmShared, setConfirmShared] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) { setLoadError('This link is incomplete. Please use the link shared with you.'); setLoading(false); return; }
        (async () => {
            const res = await nomineeCorrectionService.getByToken(token);
            if (res.error) { setLoadError(res.error); setLoading(false); return; }
            const c = res.data.correction;
            setCorrection(c);
            // Start from what's on file, so an unchanged field can simply be confirmed.
            const init: Record<string, Record<string, string>> = {};
            for (const n of c.nominees || []) {
                for (const f of n.requested_fields || []) {
                    const v = n[FIELDS[f]?.prop];
                    (init[n.nominee_id] ||= {})[f] = v == null ? '' : String(v);
                }
            }
            setValues(init);
            setLoading(false);
        })();
    }, [token]);

    const setValue = (nid: string, field: string, v: string) => {
        setValues(prev => ({ ...prev, [nid]: { ...prev[nid], [field]: v } }));
        setErrors(prev => {
            if (!prev[nid]?.[field]) return prev;
            const n = { ...prev[nid] }; delete n[field];
            return { ...prev, [nid]: n };
        });
    };

    const allocationRequested = (correction?.nominees || []).some((n: any) => n.requested_fields?.includes('allocation_pct'));
    const allocationTotal = useMemo(() => (correction?.nominees || []).reduce((sum: number, n: any) => {
        const v = n.requested_fields?.includes('allocation_pct') ? values[n.nominee_id]?.allocation_pct : n.allocation_percentage;
        const num = parseFloat(String(v ?? ''));
        return sum + (isNaN(num) ? 0 : num);
    }, 0), [correction, values]);

    const submit = async (withConfirm = confirmShared) => {
        setFormError(null);
        if (allocationRequested && Math.abs(allocationTotal - 100) > 0.01) {
            setFormError(`Allocations must add up to 100% (currently ${Math.round(allocationTotal * 100) / 100}%).`);
            return;
        }
        setSubmitting(true);
        const payload: Record<string, Record<string, string | null>> = {};
        for (const [nid, fields] of Object.entries(values)) {
            payload[nid] = Object.fromEntries(Object.entries(fields).map(([f, v]) => [f, v.trim() === '' ? null : v.trim()]));
        }
        const res = await nomineeCorrectionService.submit(token, payload, withConfirm);
        setSubmitting(false);
        if (res.code === 'CONFIRM_SHARED') { setSharedUnits(res.shared_units || {}); return; }
        if (res.error) {
            setErrors(res.errors || {});
            setFormError(res.error);
            return;
        }
        setDone(true);
    };

    const shell = (children: React.ReactNode) => (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">{children}</div>
        </div>
    );

    if (loading) return shell(<div className="flex justify-center py-24"><Loader2 className="animate-spin text-slate-400" size={28} /></div>);

    if (loadError) return shell(
        <div className="bg-white rounded-2xl border border-slate-100 shadow-md p-8 text-center">
            <AlertTriangle size={36} className="mx-auto text-amber-500 mb-3" />
            <h1 className="text-lg font-bold text-slate-800">Link not available</h1>
            <p className="text-sm text-slate-500 mt-1">{loadError}</p>
        </div>
    );

    const statusMessage: Record<string, { title: string; body: string }> = {
        SUBMITTED: { title: 'Already submitted', body: 'Thank you — we received your corrections. Our team will review them shortly.' },
        CANCELLED: { title: 'Request withdrawn', body: 'This correction request is no longer needed. No action is required from you.' },
        EXPIRED: { title: 'Link expired', body: 'This correction link has expired. Please contact our support team for a new one.' },
    };

    if (done || (correction && correction.status !== 'OPEN')) {
        const m = done
            ? { title: 'Corrections submitted', body: 'Thank you! Our team will verify the updated nominee details and let you know if anything else is needed.' }
            : statusMessage[correction.status] || statusMessage.SUBMITTED;
        return shell(
            <div className="bg-white rounded-2xl border border-slate-100 shadow-md p-8 text-center">
                <CheckCircle size={40} className={`mx-auto mb-3 ${done || correction.status === 'SUBMITTED' ? 'text-emerald-500' : 'text-slate-400'}`} />
                <h1 className="text-lg font-bold text-slate-800">{m.title}</h1>
                <p className="text-sm text-slate-500 mt-1">{m.body}</p>
            </div>
        );
    }

    const c = correction;
    const unitLabel = c.unit_index != null ? `Unit ${c.unit_index + 1}` : c.unit_id;
    const requestedNominees = (c.nominees || []).filter((n: any) => n.requested_fields?.length);
    const otherNominees = (c.nominees || []).filter((n: any) => !n.requested_fields?.length);

    const renderInput = (n: any, field: string) => {
        const def = FIELDS[field];
        const nid = n.nominee_id;
        const v = values[nid]?.[field] ?? '';
        const err = errors[nid]?.[field];
        const onChange = (raw: string) => setValue(nid, field, sanitize(def.kind, raw));
        let control: React.ReactNode;
        switch (def.kind) {
            case 'relation':
            case 'gender':
                control = (
                    <select value={v} onChange={e => onChange(e.target.value)} className={`${inputCls(err)} cursor-pointer`}>
                        <option value="">Select…</option>
                        {(def.kind === 'relation' ? RELATIONS : GENDERS).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                );
                break;
            case 'textarea':
                control = <textarea value={v} onChange={e => onChange(e.target.value)} rows={3} maxLength={500} className={`${inputCls(err)} resize-none`} />;
                break;
            case 'date':
                control = <DatePicker value={v} onChange={onChange} placeholder="DD-MM-YYYY" yearsBack={100} align="left" className="w-full" buttonClassName={`w-full rounded-xl ${err ? 'border-red-400' : ''}`} />;
                break;
            case 'doc':
                return (
                    <div key={field} className="sm:col-span-2 space-y-1.5">
                        <label className="block text-xs font-semibold text-slate-600">{def.label}</label>
                        <DocField
                            label={def.label}
                            current={n[def.prop] || undefined}
                            value={v}
                            error={err}
                            uploadPath={file => `animalkart/${storageEnv}/${c.investor_mobile}/nominee/${Date.now()}_${file.name}`}
                            onChange={url => setValue(nid, field, url)}
                        />
                    </div>
                );
            default:
                control = (
                    <div className="relative">
                        <input value={v} onChange={e => onChange(e.target.value)}
                            inputMode={['mobile', 'aadhar', 'pct'].includes(def.kind) ? 'numeric' : 'text'}
                            placeholder={def.kind === 'pan' ? 'ABCDE1234F' : undefined}
                            className={`${inputCls(err)} ${def.kind === 'pct' ? 'pr-8' : ''}`} />
                        {def.kind === 'pct' && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>}
                    </div>
                );
        }
        return (
            <div key={field} className={`space-y-1.5 ${def.kind === 'textarea' ? 'sm:col-span-2' : ''}`}>
                <label className="block text-xs font-semibold text-slate-600">{def.label}</label>
                {control}
                {def.kind === 'pct' && c.applicable_value != null && !isNaN(parseFloat(v)) && (
                    <p className="text-[11px] text-slate-400">≈ {inr((parseFloat(v) / 100) * c.applicable_value)} of {inr(c.applicable_value)}</p>
                )}
                {err && <p className="text-[11px] text-red-500 font-medium">{err}</p>}
            </div>
        );
    };

    const readOnlyRow = (label: string, value: any) => value == null || value === '' ? null : (
        <div key={label} className="min-w-0">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
            <p className="text-sm font-semibold text-slate-700 break-words">{value}</p>
        </div>
    );

    return shell(
        <div className="space-y-5">
            <div className="bg-white rounded-2xl shadow-md border border-slate-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3 bg-gradient-to-r from-slate-50 to-white">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600"><Users size={20} /></div>
                    <div className="min-w-0">
                        <h1 className="text-base font-bold text-slate-800">Update Nominee Details</h1>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                            {unitLabel} · Order {c.order_id}
                            {c.investment_value_type && <> · {valueTypeLabel(c.investment_value_type)} {inr(c.applicable_value)}</>}
                        </p>
                    </div>
                </div>
                <div className="p-6">
                    <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 flex gap-3">
                        <MessageSquareWarning size={18} className="text-orange-500 shrink-0 mt-0.5" />
                        <div className="min-w-0">
                            <p className="text-xs font-bold text-orange-800">{c.requested_by_name || 'Our team'} asked you to review the highlighted details</p>
                            <p className="text-sm text-orange-900 mt-1 whitespace-pre-wrap">{c.remarks}</p>
                        </div>
                    </div>
                </div>
            </div>

            {requestedNominees.map((n: any) => (
                <div key={n.nominee_id} className="bg-white rounded-2xl shadow-md border border-slate-100 overflow-hidden">
                    <div className="px-6 py-3 border-b border-slate-100">
                        <p className="text-sm font-bold text-slate-800">{n.nominee_full_name}{n.relation && <span className="text-slate-400 font-medium"> · {n.relation}</span>}</p>
                    </div>
                    <div className="p-6 space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {n.requested_fields.map((f: string) => FIELDS[f] && renderInput(n, f))}
                        </div>
                        {(() => {
                            const rest = Object.entries(FIELDS)
                                .filter(([f, d]) => !n.requested_fields.includes(f) && d.kind !== 'doc')
                                .map(([, d]) => readOnlyRow(d.label, d.kind === 'pct' && n[d.prop] != null ? `${n[d.prop]}%` : n[d.prop]))
                                .filter(Boolean);
                            return rest.length > 0 && (
                                <div className="pt-4 border-t border-slate-100">
                                    <p className="text-[11px] font-semibold text-slate-400 mb-3">Other details on file (no change needed)</p>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">{rest}</div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            ))}

            {allocationRequested && (
                <div className={`rounded-2xl border px-5 py-4 ${Math.abs(allocationTotal - 100) > 0.01 ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
                    <p className="text-sm font-bold text-slate-800">Allocation total: {Math.round(allocationTotal * 100) / 100}%</p>
                    <p className="text-xs text-slate-500 mt-0.5">The shares of all nominees on this unit must add up to 100%.</p>
                    {otherNominees.length > 0 && (
                        <ul className="mt-2 text-xs text-slate-600 space-y-0.5">
                            {otherNominees.map((n: any) => <li key={n.nominee_id}>{n.nominee_full_name}: {n.allocation_percentage ?? '—'}% (unchanged)</li>)}
                        </ul>
                    )}
                </div>
            )}

            {sharedUnits && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 space-y-3">
                    <div className="flex gap-2">
                        <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-bold text-amber-900">These changes also apply to your other units</p>
                            <p className="text-xs text-amber-800 mt-1">The same nominee is assigned to more units. Their personal details and documents will be updated there too:</p>
                            <ul className="text-xs text-amber-900 mt-2 space-y-0.5 list-disc pl-4">
                                {Object.entries(sharedUnits).map(([nid, units]) => {
                                    const name = (c.nominees || []).find((n: any) => n.nominee_id === nid)?.nominee_full_name || nid;
                                    return <li key={nid}><b>{name}</b>: {units.map((u: any) => u.unit_index != null ? `Unit ${u.unit_index + 1} (${u.order_id})` : u.unit_id).join(', ')}</li>;
                                })}
                            </ul>
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-amber-900 cursor-pointer">
                        <input type="checkbox" checked={confirmShared} onChange={e => setConfirmShared(e.target.checked)} className="w-4 h-4 rounded text-amber-600" />
                        I understand and want to update all of these units
                    </label>
                </div>
            )}

            {formError && <p className="text-sm font-semibold text-red-600 text-center">{formError}</p>}

            <button type="button" onClick={() => submit()} disabled={submitting || (!!sharedUnits && !confirmShared)}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 text-white text-sm font-bold rounded-xl hover:bg-emerald-700 transition-all disabled:opacity-50">
                {submitting && <Loader2 size={16} className="animate-spin" />} Submit Corrections
            </button>
        </div>
    );
};

export default NomineeCorrectionPage;
